import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Icons } from './Icons';
import './QuickPads.css';

function stripHtml(html) {
  if (!html) return '';
  let t = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/div>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<[^>]*>/g, '');
  try { t = new DOMParser().parseFromString(t, 'text/html').body.textContent || ''; } catch(e) {}
  return t.replace(/\n{3,}/g, '\n\n').trim();
}

function stripOldPrefixes(text) {
  let s = text;
  while (s.startsWith('[x] ') || s.startsWith('[X] ') || s.startsWith('[ ] ')) s = s.slice(4);
  if (s === '[ ]' || s === '[x]' || s === '[X]') s = '';
  return s;
}

function parseLine(raw) {
  if (raw.startsWith('N:')) return { type: 'N', content: raw.slice(2) };
  if (raw.startsWith('C:')) return { type: 'C', content: raw.slice(2) };
  if (raw.startsWith('X:')) return { type: 'X', content: raw.slice(2) };
  if (raw.startsWith('[x] ') || raw.startsWith('[X] ')) return { type: 'X', content: stripOldPrefixes(raw) };
  if (raw.startsWith('[ ] ')) return { type: 'C', content: stripOldPrefixes(raw) };
  return { type: 'N', content: stripOldPrefixes(raw) };
}

function valueToItems(value) {
  if (!value) return [{ type: 'N', content: '' }];
  if (value.startsWith('N:') || value.startsWith('C:') || value.startsWith('X:')) {
    return value.split('\n').map(parseLine);
  }
  const plain = stripHtml(value);
  if (!plain) return [{ type: 'N', content: '' }];
  return plain.split('\n').map(parseLine);
}

function itemsToValue(items) {
  return items.map(it => it.type + ':' + it.content).join('\n');
}

function getNumberLabel(items, idx) {
  let count = 0;
  for (let i = 0; i <= idx; i++) {
    if (items[i].type === 'N') count++;
  }
  return count;
}

/* ─── Pad Card ─────────────────────────────────────── */
function PadCard({ title, icon, value, onChange, mode, setMode, formattingEnabled }) {
  const editorRef = useRef(null);
  const isInternal = useRef(false);
  const containerRef = useRef(null);
  const [clearConfirm, setClearConfirm] = useState(false);

  // List items as direct React state
  const [listItems, setListItems] = useState(() => valueToItems(value));

  function commitItems(newItems) {
    setListItems(newItems);
    onChange(itemsToValue(newItems));
  }

  const prevMode = useRef(mode);
  useEffect(() => {
    if (mode === 'list' && prevMode.current !== 'list') {
      setListItems(valueToItems(value));
    }
    prevMode.current = mode;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (mode !== 'plain' || !editorRef.current || isInternal.current) { isInternal.current = false; return; }
    if (editorRef.current.innerHTML !== value) editorRef.current.innerHTML = value || '';
  }, [value, mode]);

  function handleRichInput() { isInternal.current = true; onChange(editorRef.current.innerHTML); }
  function execFormat(cmd) { editorRef.current.focus(); document.execCommand(cmd, false, null); handleRichInput(); }

  useEffect(() => { setClearConfirm(false); }, [mode]);

  // ═══ LIST OPERATIONS ═══
  function updateContent(i, content) {
    const list = [...listItems];
    list[i] = { ...list[i], content };
    commitItems(list);
  }

  function addItem(afterIdx, type) {
    const list = [...listItems];
    list.splice(afterIdx + 1, 0, { type: type || 'N', content: '' });
    commitItems(list);
    setTimeout(() => {
      containerRef.current?.querySelectorAll('.qp-line-input')?.[afterIdx + 1]?.focus();
    }, 20);
  }

  function removeItem(i) {
    if (listItems.length <= 1) { commitItems([{ type: 'N', content: '' }]); return; }
    const list = [...listItems];
    list.splice(i, 1);
    commitItems(list);
  }

  function toggleItemType(i) {
    const list = [...listItems];
    const item = list[i];
    if (item.type === 'N') list[i] = { ...item, type: 'C' };
    else list[i] = { ...item, type: 'N' };
    commitItems(list);
  }

  function toggleCheck(i) {
    const list = [...listItems];
    const item = list[i];
    if (item.type === 'C') list[i] = { ...item, type: 'X' };
    else if (item.type === 'X') list[i] = { ...item, type: 'C' };
    commitItems(list);
  }

  function clearAll() {
    if (!clearConfirm) { setClearConfirm(true); return; }
    commitItems([{ type: 'N', content: '' }]);
    setClearConfirm(false);
  }

  // ═══ REORDER (simple up/down buttons instead of drag) ═══
  function moveUp(i) {
    if (i === 0) return;
    const list = [...listItems];
    [list[i - 1], list[i]] = [list[i], list[i - 1]];
    commitItems(list);
  }
  function moveDown(i) {
    if (i >= listItems.length - 1) return;
    const list = [...listItems];
    [list[i], list[i + 1]] = [list[i + 1], list[i]];
    commitItems(list);
  }

  // ═══ MODE SWITCH ═══
  function enterListMode() {
    if (mode === 'list') { setMode('plain'); return; }
    const plain = stripHtml(value);
    const lines = plain ? plain.split('\n').map(l => ({ type: 'N', content: stripOldPrefixes(l) }))
      : [{ type: 'N', content: '' }];
    setListItems(lines);
    onChange(itemsToValue(lines));
    setMode('list');
  }

  function addNumberedAtEnd() {
    const list = [...listItems, { type: 'N', content: '' }];
    commitItems(list);
    setTimeout(() => {
      const els = containerRef.current?.querySelectorAll('.qp-line-input');
      els?.[els.length - 1]?.focus();
    }, 20);
  }
  function addCheckboxAtEnd() {
    const list = [...listItems, { type: 'C', content: '' }];
    commitItems(list);
    setTimeout(() => {
      const els = containerRef.current?.querySelectorAll('.qp-line-input');
      els?.[els.length - 1]?.focus();
    }, 20);
  }

  return (
    <div className="qp-card">
      <div className="qp-header">
        {icon}<span className="qp-header-title">{title}</span>
        <div className="qp-pad-tools">
          <button className={`qp-tool-btn ${mode === 'list' ? 'active' : ''}`} onClick={enterListMode} title="List mode">☰</button>
          {mode === 'list' && (
            <button className={`qp-tool-btn qp-clear-btn ${clearConfirm ? 'qp-clear-confirm' : ''}`}
              onClick={clearAll} title={clearConfirm ? 'Click again to confirm' : 'Clear all'}>
              {clearConfirm ? '✓' : '🗑'}
            </button>
          )}
        </div>
      </div>

      {mode === 'plain' && (
        <div className="qp-plain-wrap qp-resizable">
          {formattingEnabled && (
            <div className="qp-fmt-toolbar">
              <button className="qp-fmt-btn" onMouseDown={e => { e.preventDefault(); execFormat('bold'); }}><strong>B</strong></button>
              <button className="qp-fmt-btn" onMouseDown={e => { e.preventDefault(); execFormat('italic'); }}><em>I</em></button>
              <button className="qp-fmt-btn" onMouseDown={e => { e.preventDefault(); execFormat('underline'); }}><u>U</u></button>
            </div>
          )}
          <div ref={editorRef} className="qp-rich-editor" contentEditable suppressContentEditableWarning
            onInput={handleRichInput} data-placeholder="Start typing..." />
        </div>
      )}

      {mode === 'list' && (
        <div className="qp-list-wrap qp-resizable">
          <div className="qp-lines-container" ref={containerRef}>
            {listItems.map((item, i) => (
              <div key={i} className="qp-line-item">
                <div className="qp-reorder-btns">
                  <button className="qp-reorder-btn" onClick={() => moveUp(i)} disabled={i === 0} title="Move up">▲</button>
                  <button className="qp-reorder-btn" onClick={() => moveDown(i)} disabled={i >= listItems.length - 1} title="Move down">▼</button>
                </div>

                {item.type === 'N' && (
                  <span className="qp-num-label" onClick={() => toggleItemType(i)} title="Click to switch to checkbox">
                    {getNumberLabel(listItems, i)}.
                  </span>
                )}

                {(item.type === 'C' || item.type === 'X') && (
                  <button className={`qp-check-btn ${item.type === 'X' ? 'done' : ''}`}
                    onClick={() => toggleCheck(i)} title="Toggle check">
                    {item.type === 'X' ? '✓' : ''}
                  </button>
                )}

                <input className="qp-line-input"
                  type="text"
                  value={item.content}
                  onChange={e => updateContent(i, e.target.value)}
                  placeholder={item.type === 'N' ? '...' : 'Add item...'}
                  style={item.type === 'X' ? { textDecoration: 'line-through', color: 'var(--gray-400)' } : undefined}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); addItem(i, item.type === 'N' ? 'N' : 'C'); }
                    if (e.key === 'Backspace' && !item.content && listItems.length > 1) { e.preventDefault(); removeItem(i); }
                  }}
                />
                <button className="qp-line-del" onClick={() => removeItem(i)}>✕</button>
              </div>
            ))}
          </div>

          <div className="qp-add-row">
            <button className="qp-add-btn" onClick={addNumberedAtEnd} title="Add numbered item">+ Number</button>
            <button className="qp-add-btn" onClick={addCheckboxAtEnd} title="Add checkbox item">+ Checkbox</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────── */
export default function QuickPads() {
  const { currentUser, userProfile } = useAuth();
  const [collapsed, setCollapsed] = useState(() => (userProfile?.quickPadsMode || 'compact') === 'compact');
  const [searchPlan, setSearchPlan] = useState(userProfile?.searchPlan || '');
  const [generalIdeas, setGeneralIdeas] = useState(userProfile?.generalIdeas || '');
  const [spMode, setSpMode] = useState(() => {
    const m = userProfile?.searchPlanMode || 'plain';
    return (m === 'numbered' || m === 'checklist') ? 'list' : m;
  });
  const [giMode, setGiMode] = useState(() => {
    const m = userProfile?.generalIdeasMode || 'plain';
    return (m === 'numbered' || m === 'checklist') ? 'list' : m;
  });
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);
  const formattingEnabled = userProfile?.formattingEnabled === true;

  const debouncedSave = useCallback((sp, gi, spm, gim) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (!currentUser) return;
      setSaving(true);
      try { await updateDoc(doc(db, 'users', currentUser.uid), { searchPlan: sp, generalIdeas: gi, searchPlanMode: spm, generalIdeasMode: gim }); }
      catch (err) { console.error(err); }
      setSaving(false);
    }, 1200);
  }, [currentUser]);

  function spChange(v) { setSearchPlan(v); debouncedSave(v, generalIdeas, spMode, giMode); }
  function giChange(v) { setGeneralIdeas(v); debouncedSave(searchPlan, v, spMode, giMode); }
  function spModeChange(m) { setSpMode(m); debouncedSave(searchPlan, generalIdeas, m, giMode); }
  function giModeChange(m) { setGiMode(m); debouncedSave(searchPlan, generalIdeas, spMode, m); }

  if (collapsed) {
    return (
      <div className="quick-pads-collapsed" onClick={() => setCollapsed(false)}>
        <Icons.FileText size={16} /><span>Quick Notes</span><span className="qp-expand-hint">Click to expand</span>
      </div>
    );
  }

  return (
    <div className="quick-pads slide-down">
      <div className="qp-collapse-bar" onClick={() => setCollapsed(true)}>
        <div className="qp-collapse-left">
          <span className="qp-collapse-title">Quick Notes</span>
          <span className="qp-saved">{saving ? 'Saving...' : 'Saved'}</span>
        </div>
        <Icons.ChevronUp size={16} />
      </div>
      <div className="qp-grid">
        <PadCard title="SEARCH PLAN" icon={<Icons.Search size={14} />}
          value={searchPlan} onChange={spChange} mode={spMode} setMode={spModeChange} formattingEnabled={formattingEnabled} />
        <PadCard title="GENERAL IDEAS" icon={<Icons.Lightbulb size={14} />}
          value={generalIdeas} onChange={giChange} mode={giMode} setMode={giModeChange} formattingEnabled={formattingEnabled} />
      </div>
    </div>
  );
}
